// @ts-check
import axios from 'axios';
import config from '../constants/config';
import { v4 as uuidv4 } from 'uuid';

const invoicesAxios = axios.create();
const invoicesService = {};
/**
 *
 * @param {import('../../../types/invoices').UpdateInvoicePayload} payload
 * @param {import('../../../types/invoices').UpdateInvoiceOptions} options
 * @returns {import('axios').AxiosPromise}
 */
invoicesService.createInvoice = (payload, options) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/invoices/`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Correlation-ID': uuidv4(),
  };

  return invoicesAxios.post(url, payload, { headers, params: options });
};

/**
 *
 * @param {string} invoiceId
 * @param {import('../../../types/invoices').UpdateInvoicePayload} payload
 * @param {import('../../../types/invoices').UpdateInvoiceOptions} options
 * @returns {import('axios').AxiosPromise}
 */
invoicesService.updateInvoice = (invoiceId, payload, options) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/invoices/${invoiceId}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Correlation-ID': uuidv4(),
  };

  return invoicesAxios.put(url, payload, { headers, params: options });
};

/**
 * Delete an invoice
 * @param {string} invoiceId
 * @returns {import('axios').AxiosPromise}
 */
invoicesService.deleteInvoice = (invoiceId) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/invoices/${invoiceId}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': uuidv4(),
  };

  return invoicesAxios.delete(url, { headers });
};

invoicesService.generateUniqueInvoiceNumber = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/invoices/generateUnique`;
  const headers = {
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': uuidv4(),
  };

  return invoicesAxios.get(url, { headers });
};

/**
 *
 * @param {string} invoiceNum
 * @returns {import('axios').AxiosPromise}
 */
invoicesService.checkIfInvoiceNumberIsUse = (invoiceNum) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/invoices/isUnique/${invoiceNum}`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  return invoicesAxios.get(url, { headers });
};

/**
 *
 * @param {string} invoiceId
 * @param {string} serialNum
 * @returns {import('axios').AxiosPromise}
 */
invoicesService.sendPaxSaleRequestWithInvoice = (invoiceId, serialNum) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/invoices/paxSaleRequest/${invoiceId}`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const payload = {
    serialNum,
  };
  return invoicesAxios.post(url, payload, { headers });
};

/**
 *
 * @param {*} data
 * @param {string} serialNum
 * @returns {import('axios').AxiosPromise}
 */
invoicesService.sendPaxSaleRequest = (data, serialNum) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/invoices/paxSaleRequest/`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const payload = {
    ...data,
    serialNum,
  };
  return invoicesAxios.post(url, payload, { headers });
};

/**
 * Get pending invoices list (old)
 * @param {{companyId: string, permissions: Object, userId: string, locationId: string}} params
 * @returns
 */
invoicesService.getPendingInvoicesList = (params) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}getPendingStatusInvoiceList/`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  return invoicesAxios.get(url, { headers, params });
};

/**
 * Get all invoices list for this location
 * Dates should be ISO strings
 * @param {import('../../../types/invoices').GetInvoicesListOptions} params
 * @returns
 */
invoicesService.getLocationInvoicesList = (params) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const url = `${config.BASEURL}v2/invoices/`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  return invoicesAxios.get(url, { headers, params });
};

/**
 * Gets the file and serves it to the front end
 * @param {import('../../../types/common').ObjectIdType} invoiceId
 * @param {string} fileName
 * @returns
 */
invoicesService.getInvoiceFile = (invoiceId, fileName, openInBrowser = false) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token || '';
  const encodedFileName = encodeURIComponent(fileName);
  const url = `${config.BASEURL}v2/invoices/${invoiceId}/files/${encodedFileName}`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  return invoicesAxios.get(url, { headers, responseType: 'blob' }).then((response) => {
    if (openInBrowser) {
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    }
  });
};

export default invoicesService;
